// Season-finale superlatives.
//
// These are SUBJECT-based: they describe how well the league knew each person,
// which is a different question from how well each person played (that is
// `seasonHighlights` in format.ts, used on the home screen).
//
// Tone rule: these name people warmly. "Biggest mystery" is a joke about the
// league not keeping up, never a judgement of the person. Nothing here is ever
// framed as someone being a bad friend.

export interface SubjectStat {
  subject_user_id: string;
  asked: number; // questions about them this season
  attempts: number; // guesses made about them (sit-outs excluded)
  correct: number; // of those guesses, how many landed
}

export interface PlayerSpeed {
  user_id: string;
  correct_count: number;
  avg_correct_response_ms: number | null;
}

export interface Superlative {
  label: string;
  name: string;
  detail: string;
}

// Below this, a single lucky or unlucky question decides the title, which makes
// it meaningless and occasionally unkind.
const MIN_ASKED = 2;
const MIN_ATTEMPTS = 3;

export function seasonSuperlatives(
  subjects: SubjectStat[],
  speeds: PlayerSpeed[],
  nameOf: (userId: string) => string
): Superlative[] {
  const out: Superlative[] = [];
  const eligible = subjects.filter((s) => s.asked >= MIN_ASKED && s.attempts >= MIN_ATTEMPTS);

  if (eligible.length >= 2) {
    const rate = (s: SubjectStat) => s.correct / s.attempts;
    const ranked = [...eligible].sort((a, b) => rate(b) - rate(a) || b.attempts - a.attempts);
    const best = ranked[0];
    const worst = ranked[ranked.length - 1];

    // Only worth naming when the two ends actually differ.
    if (best.subject_user_id !== worst.subject_user_id && rate(best) > rate(worst)) {
      out.push({
        label: "Most known",
        name: nameOf(best.subject_user_id),
        detail: `${Math.round(rate(best) * 100)}% of guesses about them were right`,
      });
      out.push({
        label: "Biggest mystery",
        name: nameOf(worst.subject_user_id),
        detail: `Only ${Math.round(rate(worst) * 100)}% of guesses landed. Y'all need to catch up.`,
      });
    }
  }

  const quickest = speeds
    .filter((p) => p.correct_count > 0 && p.avg_correct_response_ms != null)
    .sort((a, b) => a.avg_correct_response_ms! - b.avg_correct_response_ms!)[0];
  if (quickest) {
    out.push({
      label: "Quick draw",
      name: nameOf(quickest.user_id),
      detail: `${(quickest.avg_correct_response_ms! / 1000).toFixed(1)}s average on correct answers`,
    });
  }

  return out;
}
